import React, { useContext, useState, useCallback, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { DataManagementShell } from './DataManagementShell'
import {
  Content,
  Data,
  Navigation,
  Responsive,
  Search,
  Modal,
  Indicators,
} from 'ui'
import { truncate, wait } from 'utils'
import { usePaginatedSchools } from './usePaginatedSchools'
import { Link } from 'react-router-dom'
import { useDeleteSchoolsMutation, useMakeSchoolMutation } from 'epc-schema'
import {
  BatchActionForm,
  BatchActionSubmitHandler,
  BATCH_ACTION_DELETE,
  BATCH_ACTION_EDIT,
  NewSchoolFormValues,
  NewSchoolForm,
} from '../../../Forms'
import { toast } from 'react-hot-toast'
import { useQuery } from '../../../routes'

const DELETE_STYLES = 'opacity-50 pointer-events-none'

const { Table, Cell, Row, Header } = Data

const MainContent: React.FC = () => {
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('expanded')

  const [deletedIds, setDeletedIds] = useState<string[]>([])

  const [selection, setSelection] = useState<{
    selected?: string[]
    currentPage?: number
  }>({})
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [editing, setEditing] = useState(false)

  const {
    search: { refresh },
    set: setRefreshQuery,
  } = useQuery<{ refresh?: string }>()

  const {
    currentPage,
    error,
    loading,
    schools,
    handlePageClick,
    pageCount,
    queryString,
    setQueryString,
    facets,
    toggleFacet,
    appliedFacets,
    refetch,
    updatingIndex,
    percentUpdated,
  } = usePaginatedSchools({})

  const [deleteSchools, { loading: loadingDelete }] = useDeleteSchoolsMutation()

  const hasSelection = (schools?.length ?? -1) > 0
  const hasSelectedItems = selection.selected?.length
  const selectedAll =
    hasSelection && selection.selected?.length === schools?.length

  const resetSelection = useCallback(
    () => setSelection({ currentPage, selected: [] }),
    [currentPage, setSelection]
  )

  useEffect(() => {
    if (selection.currentPage !== currentPage) {
      resetSelection()
    }
  }, [currentPage, selection, resetSelection])

  const handleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelection({
        currentPage,
        selected: [],
      })
    } else {
      setSelection({
        currentPage,
        selected: schools?.map((s) => s?.id as string),
      })
    }
  }

  const handleToggleSchool = (id?: string) => (checked: boolean) => {
    if (!checked) {
      setSelection({
        currentPage,
        selected: selection.selected?.filter((i) => i !== id),
      })
    } else {
      setSelection({
        currentPage,
        selected: [...(selection.selected ?? []), id as string],
      })
    }
  }

  const handleBatchAction: BatchActionSubmitHandler = async (data, _action) => {
    switch (data.action) {
      case BATCH_ACTION_EDIT:
        setEditing(true)
        break
      case BATCH_ACTION_DELETE:
        setConfirmDelete(true)
        break
    }
    return undefined
  }

  const [pendingSchools, setPendingSchools] = useState<{
    [key: string]: NewSchoolFormValues
  }>({})

  const handleDismissDialog = useCallback(() => {
    setConfirmDelete(false)
    setEditing(false)
    setPendingSchools({})
  }, [setPendingSchools, setEditing, setConfirmDelete])

  const handleDeleteAll = async () => {
    await deleteSchools({ variables: { ids: selection.selected } })
    setDeletedIds([...deletedIds, ...(selection.selected ?? [])])
    resetSelection()
    setConfirmDelete(false)
    toast.success(
      'Delete operation successful. It may take a few minutes for these changes to be reflected in the index.',
      { duration: 5000 }
    )
  }

  const handleSchoolUpdate = useCallback(
    (values: NewSchoolFormValues) => {
      setPendingSchools({
        ...pendingSchools,
        [values.overwriteId ?? '0']: values,
      })
    },
    [pendingSchools, setPendingSchools]
  )

  const [makeSchool, { loading: schoolLoading }] = useMakeSchoolMutation()
  const handleConfirm = useCallback(async () => {
    await Object.values(pendingSchools).reduce(async (p, input) => {
      await p
      if (input.overwriteId) {
        await makeSchool({ variables: { input } })
      }
    }, Promise.resolve())
    toast.success(
      'Update complete! It may take a few minutes for these changes to be reflected in the index.',
      { duration: 5000 }
    )
    handleDismissDialog()
  }, [pendingSchools, handleDismissDialog, makeSchool])

  useEffect(() => {
    const reload = async () => {
      if (refresh === 'true') {
        await wait(2)
        setRefreshQuery({})
        refetch()
      }
    }
    reload()
  })

  return (
    <>
      <Helmet>
        <title>Schools - MCSS Pro Connect</title>
      </Helmet>
      <div className="flex flex-col flex md:flex-row flex relative">
        <div className="w-full md:w-1/3 lg:w-1/4 md:mr-2">
          <Search.FiltersCard
            onToggle={toggleFacet}
            onQuery={setQueryString}
            facets={facets ?? {}}
            appliedFacets={appliedFacets}
            initialQueryString={queryString}
            maxFilterBeforeReveal={5}
            attributeTransforms={{ city: 'County' }}
          />
        </div>
        <div className="w-full md:w-2/3 lg:w-3/4">
          <div className="border-b mb-4 pb-4">
            <NewSchoolForm />
          </div>
          {hasSelectedItems ? (
            <BatchActionForm
              onSubmit={handleBatchAction}
              defaultValues={{ action: BATCH_ACTION_EDIT }}
            />
          ) : null}
          <Table>
            <Row>
              <Header width="10%" className="!text-secondary">
                {/* <Input.CheckBox
                  name="header-row"
                  checked={selectedAll}
                  value={'selectAll'}
                  onChange={handleSelectAll}
                /> */}
              </Header>
              <Header width="30%" className="!text-secondary">
                School
              </Header>
              <Header width="30%" className="!text-secondary">
                District
              </Header>
              <Header width="20%" className="!text-secondary">
                County
              </Header>
            </Row>
            {schools ? (
              schools.map((e) => (
                <Row
                  key={e?.id}
                  className={
                    deletedIds.includes(e?.id ?? '') ? DELETE_STYLES : ''
                  }
                >
                  <Header width="10%" className="!text-secondary">
                    {/* {deletedIds.includes(e?.id ?? '') ? null : (
                      <Input.CheckBox
                        name="header-row"
                        checked={selection.selected?.includes(e?.id ?? '')}
                        value={'selectAll'}
                        onChange={handleToggleSchool(e?.id)}
                      />
                    )} */}
                  </Header>
                  <Cell width="30%">{truncate(e?.name ?? '', 50)}</Cell>
                  <Cell width="30%">{e?.district?.name}</Cell>
                  <Cell width="20%">{e?.city?.name}</Cell>
                </Row>
              ))
            ) : error ? (
              <Row>
                {/* <Form.ErrorComponent
                  error={
                    error
                      ? error.message
                      : 'There was a problem fetching schools.'
                  }
                /> */}
              </Row>
            ) : (
              <Row>
                <Cell>No results found.</Cell>
              </Row>
            )}
          </Table>
        </div>
        {updatingIndex ? (
          <div className="absolute inset-0 rounded bg-contentareabackgroundalt bg-opacity-75 flex flex-col flex">
            <article className="flex flex-col flex m-auto rounded bg-white shadow p-2 px-4 text-center">
              <Content.Body>
                This index is currently being updated. Please be patient.{' '}
                <span className="font-bold text-actionable">
                  ({percentUpdated}% complete)
                </span>
              </Content.Body>
              <Content.Body className="-mt-2">
                <span className="text-xs italic text-black-200">
                  If you're wiping existing data it could be a few minutes
                  before the import progress begins to update.
                </span>
              </Content.Body>
            </article>
          </div>
        ) : null}
      </div>

      {loading ? null : (
        <Navigation.SimplePaginationBar
          onPageClick={handlePageClick({ historyPush: true })}
          currentPage={currentPage ?? 0}
          pageCount={pageCount ?? 0}
        />
      )}
      {editing ? (
        <Modal.View onDismiss={handleDismissDialog} size={Modal.Size.default}>
          <div className="flex flex-col flex w-full -mt-4">
            <Content.HeadingSmall>Edit Districts</Content.HeadingSmall>
            {schools
              ?.filter((s) => selection.selected?.includes(s?.id ?? ''))
              .map((s) => (
                <div key={s?.id} className="flex w-full">
                  <NewSchoolForm
                    hideTitle
                    hideSubmit
                    defaultValues={{
                      school: s?.name ?? '',
                      district: s?.district?.name,
                      city: s?.city?.name,
                      overwriteId: s?.id,
                    }}
                    onChange={handleSchoolUpdate}
                  />
                </div>
              ))}
            {/* <Form.ButtonGroup direction="row" className="ml-auto">
              <Form.Button onClick={handleDismissDialog} appearance="secondary">
                <span>Cancel</span>
              </Form.Button>
              <Form.Button onClick={handleConfirm} appearance="actionable">
                <span>Update Schools</span>
              </Form.Button>
            </Form.ButtonGroup> */}
          </div>
          {schoolLoading ? <Indicators.AbsoluteOverlay /> : null}
        </Modal.View>
      ) : null}
      {confirmDelete ? (
        <Modal.Dialog
          onDismiss={handleDismissDialog}
          onConfirm={handleDeleteAll}
          title="Are you sure?"
          message={`You are about to delete ${
            selection.selected?.length ?? 0
          } schools.`}
          confirmLabel="Delete"
          loading={loadingDelete}
          destructive
        />
      ) : null}
    </>
  )
}

export const DistrictListView = () => (
  <DataManagementShell
    returnUrl="/admin/management/districts"
    linkText="back to district quick actions"
    heading="Schools / Districts"
    main={<MainContent />}
    aside={
      <>
        <Link to="/admin/management/districts/import">
          <Content.ActionButton label="Import Bulk Data" />
        </Link>
      </>
    }
  />
)
